import { useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { HierarchyTree, type HierarchyTreeNode } from '../hierarchy/HierarchyTree'
import { iconPathFor } from '../../lib/asset-type-icons'
import {
  ancestorUids,
  buildInheritanceTree,
  buildPossibleChildrenTree,
  findTreeNode,
  possibleChildTypes,
  type TypeNodeInput,
  type TypeTreeNode,
} from '../../lib/asset-type-tree'

export type AssetTypeTreeMode = 'inheritance' | 'children'

export interface HierarchyAssetType extends TypeNodeInput {
  code?: string
  icon?: string | null
  name?: unknown
}

interface Props<T extends HierarchyAssetType> {
  org: string
  types: T[]
  mode: AssetTypeTreeMode
  onModeChange: (mode: AssetTypeTreeMode) => void
  getName: (type: T) => string
  /** The type a detail page is about: the trees are cut around it. */
  focusUid?: string
  /** List-page search: keep the branches that lead to a matching type. */
  query?: string
  matches?: (type: T, query: string) => boolean
}

// Node uids are type codes joined by '>' along the path from the root: the
// possible-children tree repeats a type under several parents (equipment under
// a housing and under its rooms), so a type uid alone is not unique there.
const SEP = '>'

function flatten<T extends HierarchyAssetType>(
  roots: TypeTreeNode<T>[],
  getName: (t: T) => string,
  out: HierarchyTreeNode[] = [],
  parentPath?: string,
): HierarchyTreeNode[] {
  for (const n of roots) {
    const path = parentPath ? `${parentPath}${SEP}${n.type.code ?? n.type.uid}` : (n.type.code ?? n.type.uid)
    out.push({
      uid: path,
      parentUid: parentPath ?? null,
      label: getName(n.type),
      icon: iconPathFor(n.type.icon),
      badge: n.type.code,
    })
    flatten(n.children, getName, out, path)
  }
  return out
}

function prune<T extends HierarchyAssetType>(nodes: TypeTreeNode<T>[], keep: (t: T) => boolean): TypeTreeNode<T>[] {
  const out: TypeTreeNode<T>[] = []
  for (const n of nodes) {
    const children = prune(n.children, keep)
    if (keep(n.type) || children.length > 0) out.push({ type: n.type, children })
  }
  return out
}

/**
 * The ontology's two hierarchies in one tree (spec 2026-10-09-04, issue #1469):
 * **inheritance** (a type and its subtypes) and **possible children** (the
 * types an asset of a type may hold), switched by a toggle the page keeps in
 * its URL. Read-only: a node opens its type page.
 */
export function AssetTypeHierarchy<T extends HierarchyAssetType>({
  org, types, mode, onModeChange, getName, focusUid, query, matches,
}: Props<T>) {
  const { t } = useTranslation('types')
  const navigate = useNavigate()

  const roots = useMemo(() => {
    const label = (x: T) => getName(x)
    if (mode === 'inheritance') {
      const forest = buildInheritanceTree(types, label)
      if (!focusUid) return forest
      // A type page: the chain of its ancestors, then its own subtree.
      const byUid = new Map(types.map((x) => [x.uid, x]))
      const chain = ancestorUids(focusUid, byUid).reverse()
      const own = findTreeNode(forest, focusUid)
      if (!own) return []
      let node: TypeTreeNode<T> = own
      for (let i = chain.length - 2; i >= 0; i--) {
        const ancestor = byUid.get(chain[i])
        if (!ancestor) break
        node = { type: ancestor, children: [node] }
      }
      return [node]
    }
    if (focusUid) {
      // One level: the types an asset of this type holds. Each opens its own
      // page for the next level, which keeps the card readable (a housing
      // holds ~30 types, each room ~20 more).
      const tree = buildPossibleChildrenTree(focusUid, types, label, 1)
      return tree ? [tree] : []
    }
    // The list page: the trees of the types that have no parent of their own
    // and hold something (the site, the parcel...).
    const byUid = new Map(types.map((x) => [x.uid, x]))
    const tops = types.filter((x) => ancestorUids(x.uid, byUid)
      .every((uid) => (byUid.get(uid)?.possible_parent_asset_type_uids ?? []).length === 0)
      && possibleChildTypes(x.uid, types).length > 0)
    return tops
      .map((x) => buildPossibleChildrenTree(x.uid, types, label, 2))
      .filter((n): n is TypeTreeNode<T> => n !== undefined)
      .sort((a, b) => label(a.type).localeCompare(label(b.type)))
  }, [types, mode, focusUid, getName])

  const filtered = useMemo(() => {
    const q = query?.trim().toLowerCase()
    if (!q || !matches) return roots
    return prune(roots, (x) => matches(x, q))
  }, [roots, query, matches])

  const nodes = useMemo(() => flatten(filtered, getName), [filtered, getName])
  const byCode = useMemo(() => new Map(types.map((x) => [x.code, x])), [types])

  const modeToggle = (
    <div role="group" aria-label={t('asset_types.tree_mode_aria')} className="flex rounded-md border p-0.5">
      {(['inheritance', 'children'] as const).map((m) => (
        <Button
          key={m}
          variant={mode === m ? 'secondary' : 'ghost'}
          size="sm"
          aria-pressed={mode === m}
          onClick={() => onModeChange(m)}
          data-testid={`asset-type-tree-mode-${m}`}
        >
          {t(`asset_types.tree_mode_${m}`)}
        </Button>
      ))}
    </div>
  )

  return (
    <div className="space-y-2" data-testid="asset-type-hierarchy" data-mode={mode}>
      <p className="px-3 pt-3 text-sm text-muted-foreground">{t(`asset_types.tree_description_${mode}`)}</p>
      <HierarchyTree
        // Expansion is initialised once per mount: remount when the set of
        // nodes changes shape, so every branch of the new tree starts open.
        key={`${mode}:${focusUid ?? ''}:${query ?? ''}`}
        nodes={nodes}
        title={t('asset_types.tree_title')}
        headerActions={modeToggle}
        // The list's possible-children forest repeats equipment under every
        // container: it starts folded.
        defaultExpanded={mode === 'children' && !focusUid ? 'none' : 'all'}
        selectedUid={focusUid ? nodes.find((n) => byCode.get(n.uid.split(SEP).pop())?.uid === focusUid)?.uid : undefined}
        onSelect={(path) => {
          const type = byCode.get(path.split(SEP).pop())
          if (type) navigate({ to: '/$org/asset-types/$assetTypeUid', params: { org, assetTypeUid: type.uid } })
        }}
        testIdPrefix="asset-type-tree"
        labels={{
          edit: '', done: '', root: '', dropToRoot: '', addChild: '',
          empty: query ? t('asset_types.tree_no_match') : t('asset_types.tree_empty'),
        }}
      />
    </div>
  )
}
